/**
 * Lane C0-PROPS: the plains' big and sacred buildings (C0_TOWN_LAYOUT §2): หอธงสำนัก (the banner hall), โรงฝึกยุทธ์
 * (the training hall), the two-storey inn, the opera stage, the ruined temple and its bell tower, the garden pagoda,
 * the shrines and the wayside pavilion. Red-pillared halls on stone bases with balustrades and centre stairs, painted
 * bracket bands, key-pattern trims, hip-and-gable roofs with gold horn tips, ridge beasts and red tassels.
 */
import { hash2, valueNoise } from '@nine/shared';
import { box, door, faceRec, lantern, lattice, M, plaque, snap, wall, type Ramp } from '../buildings';
import type { Sprite } from '../scenery';
import { podiumStage } from './podium';
import { faces, hipRoof, horns, plasterWalls, plinthK, timber, trim, windows, type Rect, type Stage } from './common';
import { blob, brackets, clothBanner, column, cornerTassels, done, finial, hipGable, K, paintedBeam, rail, ROOF_TILES, stage, steps, type RoofColour } from './parts';

/** A colonnade of `n` columns along the front-left face at y, from u0 to u1. */
function colonnade(s: Stage, y: number, u0: number, u1: number, n: number, z0: number, z1: number, mat: Ramp = K.maroon, r = 0.13): void {
  for (let k = 0; k < n; k++) column(s.px, s.P, u0 + ((u1 - u0) * k) / (n - 1), y, r, z0, z1, mat);
}
/** Columns down the front-right face at x, from v0 to v1. */
function colonnadeR(s: Stage, x: number, v0: number, v1: number, n: number, z0: number, z1: number, mat: Ramp = K.maroon, r = 0.13): void {
  for (let k = 0; k < n; k++) column(s.px, s.P, x, v0 + ((v1 - v0) * k) / (n - 1), r, z0, z1, mat);
}
/** Full-height lattice doors along a face between u0 and u1 in `n` leaves. */
function leaves(s: Stage, side: 'left' | 'right', at: number, u0: number, u1: number, z0: number, z1: number, n: number, eave: number): void {
  const w = (u1 - u0) / n;
  for (let k = 0; k < n; k++) {
    const a = u0 + w * k + 0.04;
    const b = u0 + w * (k + 1) - 0.04;
    wall(s.px, s.P, side, at, a, b, z0, z0 + (z1 - z0) * 0.3, (u, v, l) => snap(K.maroon, l + ((v | 0) % 6 === 0 ? -0.2 : 0.05), u | 0, v | 0, 0));
    lattice(s.px, s.P, side, at, a + 0.03, b - 0.03, z0 + (z1 - z0) * 0.32, z1 - 3, 0.5, eave);
  }
}

/**
 * หอธงสำนัก (24 × 14, ≤ 200 px, grey): a two-step stone base with a white balustrade and a wide centre stair, a red
 * colonnade on a deep porch, lattice doors, a bracket band, a great hip-and-gable roof with ridge beasts, the sect
 * plaque under the eave, lanterns and corner tassels.
 */
export function bannerHall(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 206, 40);
  const tiles = ROOF_TILES[colour];
  const base = 18;
  box(s.px, s.P, [0.2, 0.2, w - 0.2, h - 0.2], 0, 9, (u, v, l) => snap(M.stone, l - 0.05 + (hash2(u | 0, (v / 5) | 0) - 0.5) * 0.15, u | 0, v | 0, 0.2), (u, v, l) => snap(M.stone, l, u | 0, v | 0, 0.2));
  plinthK(s, [0.8, 0.8, w - 0.8, h - 0.8], base);
  // The balustrade round the base's front edges, open at the stair.
  const st0 = w / 2 - 3;
  const st1 = w / 2 + 3;
  rail(s.px, s.P, 'left', h - 0.85, 0.85, st0, base, 9, M.linen, 4);
  rail(s.px, s.P, 'left', h - 0.85, st1, w - 0.85, base, 9, M.linen, 4);
  rail(s.px, s.P, 'right', w - 0.85, 0.85, h - 0.85, base, 9, M.linen, 4);
  // The hall body behind a porch.
  const r: Rect = [1.6, 1.4, w - 1.6, h - 3.2];
  const z1 = 104;
  plasterWalls(s, r, base, z1, 'none', z1 + 4);
  leaves(s, 'left', r[3], r[0] + 0.4, r[2] - 0.4, base, z1 - 14, 14, z1);
  windows(s, 'right', r[2], r[1], r[3], base + 30, z1 - 22, 4, 0.48, z1);
  faces(s, r, z1 - 14, z1, paintedBeam(z1 - 14, z1, 22));
  // The porch ceiling's shade line and the colonnade at its front.
  colonnade(s, h - 1.7, 1.6, w - 1.6, 9, base, z1);
  colonnadeR(s, w - 1.6, 1.4, h - 1.7, 5, base, z1);
  faces(s, [1.5, 1.3, w - 1.5, h - 1.6], z1, z1 + 12, brackets(z1, z1 + 12));
  trim(s, [1.5, 1.3, w - 1.5, h - 1.6], z1 - 6);
  // The plaque in the middle bay, lanterns in the outer bays.
  plaque(s.px, s.P, 'left', h - 1.55, w / 2 - 2.0, w / 2 + 2.0, z1 - 30, z1 - 12, K.indigo);
  for (const u of [w / 2 - 5.5, w / 2 + 5.5, w / 2 - 9, w / 2 + 9]) lantern(s.px, s.P(u, h - 1.6, z1 - 4), 4);
  steps(s.px, s.P, 'left', h - 0.8, st0, st1, base, 6, 0.14);
  // Stone lions either side of the stair.
  for (const u of [st0 - 0.4, st1 + 0.4]) {
    box(s.px, s.P, [u - 0.3, h - 0.6, u + 0.3, h], 0, 10, (uu, v, l) => snap(M.stone, l, uu | 0, v | 0, 0.2));
    blob(s.px, ...s.P(u, h - 0.3, 17), 4, 6, M.stone, 0.6);
  }
  hipGable(s.px, s.P, [0.4, 0.2, w - 0.4, h - 0.9], z1 + 12, 3.2, 160, 196, tiles);
  horns(s, [0.4, 0.2, w - 0.4, h - 0.9], z1 + 12, tiles, 1.5);
  cornerTassels(s.px, s.P, [0.4, 0.2, w - 0.4, h - 0.9], z1 + 12);
  return done(s);
}

/**
 * โรงฝึกยุทธ์ (14 × 6, ≤ 150 px, grey): an open-fronted hall on a low base, weapon racks along its back wall, a drum
 * on a stand at its east end, red columns, a bracket band, a hip-and-gable roof.
 */
export function trainingHall(w: number, h: number, colour: RoofColour): Sprite {
  const s = podiumStage(w, h, 156, 34, 8);
  const tiles = ROOF_TILES[colour];
  const base = 10;
  plinthK(s, [0.15, 0.15, w - 0.15, h - 0.15], base);
  // The back wall, seen through the open front: racks of spears and staffs, a banner of the art taught.
  wall(s.px, s.P, 'left', 3.0, 0.6, w - 0.6, base, 86, (u, v) => snap(M.wood, 0.3 + ((u | 0) % 6 === 0 ? -0.15 : 0) + (v < base + 8 ? -0.1 : 0) - (v > 70 ? 0.15 : 0), u | 0, v | 0));
  for (let k = 0; k < 5; k++) {
    const c = 1.6 + k * ((w - 3.2) / 4);
    if (k === 2) continue;
    wall(s.px, s.P, 'left', 3.02, c - 0.8, c + 0.8, base + 6, 72, (u, v) => {
      const x = Math.round(u) % 4;
      if (Math.abs(v - (base + 16)) < 1.5 || Math.abs(v - (base + 50)) < 1.5) return M.deck[3]!;
      if (x === 0) return v > 60 ? (v > 66 ? K.iron[5]! : M.red[3]!) : M.deck[4]!;
      if (x === 1) return v > 66 ? K.iron[3]! : M.deck[2]!;
      return null;
    });
  }
  wall(s.px, s.P, 'left', 3.02, w / 2 - 0.7, w / 2 + 0.7, base + 14, 76, (u, v, l) => snap(M.red, l + 0.2 + (valueNoise(u, v / 5) - 0.5) * 0.1, u | 0, v | 0, 0));
  wall(s.px, s.P, 'left', 3.03, w / 2 - 0.3, w / 2 + 0.3, base + 34, base + 52, (u, v) => (hash2(u | 0, v | 0) > 0.45 ? M.gold[3]! : M.red[2]!));
  // The east end wall and the floor mats.
  wall(s.px, s.P, 'right', w - 0.6, 0.7, h - 1.4, base, 86, (u, v, l) => snap(M.plaster, l + ((v | 0) % 30 === 0 ? -0.2 : 0) - (v > 80 ? 0.3 : 0), u | 0, v | 0));
  windows(s, 'right', w - 0.6, 0.7, h - 1.4, base + 26, 70, 2, 0.4, 92);
  colonnade(s, h - 1.0, 0.7, w - 0.7, 6, base, 86);
  faces(s, [0.6, 0.6, w - 0.6, h - 0.9], 86, 96, brackets(86, 96));
  trim(s, [0.6, 0.6, w - 0.6, h - 0.9], 80);
  plaque(s.px, s.P, 'left', h - 0.85, w / 2 - 1.5, w / 2 + 1.5, 64, 78, M.dark);
  // The drum on its stand at the east end.
  const [dx, dy] = s.P(w - 0.9, h - 0.5, 26);
  for (let b = -9; b <= 9; b++)
    for (let a = -6; a <= 6; a++)
      if ((a * a) / 36 + (b * b) / 81 <= 1) s.px.set(dx + a, dy + b, (a * a) / 16 + (b * b) / 49 <= 1 ? snap(K.cream, 0.6 + a * 0.04, a, b, 0) : snap(M.red, 0.5, a, b, 0));
  for (const o of [-7, 7]) for (let y = 0; y < 26; y++) s.px.set(dx + o, dy + 9 - (y - 17), M.wood[2]!);
  steps(s.px, s.P, 'left', h - 0.15, w / 2 - 1.6, w / 2 + 1.6, base, 2, 0.15);
  hipGable(s.px, s.P, [0.0, 0.0, w, h - 0.3], 96, 1.6, 124, 148, tiles);
  horns(s, [0.0, 0.0, w, h - 0.3], 96, tiles, 1.1);
  cornerTassels(s.px, s.P, [0.0, 0.0, w, h - 0.3], 96);
  return done(s);
}

/**
 * The two-storey inn (โรงเตี๊ยม, 16 × 12, ≤ 170 px, red): a ground floor of red posts and lattice doors with a long
 * signboard, a tiled skirt, an upper storey with a balcony rail and lanterns, a hip-and-gable roof, the wine banner.
 */
export function inn(w: number, h: number, colour: RoofColour): Sprite {
  const s = podiumStage(w, h, 176, 36, 8);
  const tiles = ROOF_TILES[colour];
  plinthK(s, [0.15, 0.15, w - 0.15, h - 0.15], 8);
  const r: Rect = [0.5, 0.5, w - 0.5, h - 0.7];
  const z1 = 70;
  plasterWalls(s, r, 8, z1, 'none', z1 + 8);
  leaves(s, 'left', r[3], w / 2 - 3.2, w / 2 + 3.2, 8, z1 - 10, 6, z1 + 8);
  windows(s, 'left', r[3], r[0], w / 2 - 3.4, 26, 54, 3, 0.55, z1 + 8);
  windows(s, 'left', r[3], w / 2 + 3.4, r[2], 26, 54, 3, 0.55, z1 + 8);
  windows(s, 'right', r[2], r[1], r[3], 26, 54, 5, 0.55, z1 + 8);
  colonnade(s, r[3] + 0.02, r[0], r[2], 9, 8, z1, M.lacquer, 0.1);
  colonnadeR(s, r[2] + 0.02, r[1], r[3], 7, 8, z1, M.lacquer, 0.1);
  plaque(s.px, s.P, 'left', r[3] + 0.12, w / 2 - 2.6, w / 2 + 2.6, z1 - 10, z1 + 2, M.dark);
  hipRoof(s, [r[0] - 0.45, r[1] - 0.45, r[2] + 0.45, r[3] + 0.45], z1 + 2, [r[0] + 0.2, r[1] + 0.2, r[2] - 0.2, r[3] - 0.2], z1 + 12, tiles, { ridge: false, horns: 0.8 });
  const u: Rect = [0.9, 0.9, w - 0.9, h - 1.3];
  const z2 = 126;
  plasterWalls(s, u, z1 + 12, z2, 'none', z2 + 4);
  timber(s, 'left', u[3], u[0], u[2], z1 + 12, z2, 1.6, [z2 - 4], K.maroon, z2 + 4);
  timber(s, 'right', u[2], u[1], u[3], z1 + 12, z2, 1.6, [z2 - 4], K.maroon, z2 + 4);
  windows(s, 'left', u[3], u[0], u[2], z1 + 18, z2 - 8, 9, 0.6, z2 + 4);
  windows(s, 'right', u[2], u[1], u[3], z1 + 18, z2 - 8, 6, 0.6, z2 + 4);
  rail(s.px, s.P, 'left', u[3] + 0.35, u[0] - 0.2, u[2] + 0.35, z1 + 12, 11, M.lacquer);
  rail(s.px, s.P, 'right', u[2] + 0.35, u[1] - 0.2, u[3] + 0.35, z1 + 12, 11, M.lacquer);
  faces(s, u, z2 - 8, z2, paintedBeam(z2 - 8, z2, 18));
  for (let k = 0; k < 7; k++) lantern(s.px, s.P(1.6 + k * ((w - 3.2) / 6), u[3] + 0.4, z2 - 2), 3);
  for (let k = 0; k < 5; k++) lantern(s.px, s.P(u[2] + 0.4, 1.6 + k * ((h - 3.4) / 4), z2 - 2), 3);
  hipGable(s.px, s.P, [0.1, 0.1, w - 0.1, h - 0.6], z2, 2.4, 148, 168, tiles);
  horns(s, [0.1, 0.1, w - 0.1, h - 0.6], z2, tiles, 1.2);
  cornerTassels(s.px, s.P, [0.1, 0.1, w - 0.1, h - 0.6], z2);
  // The wine banner on its pole at the front corner.
  const [qx, qy] = s.P(w - 0.2, h - 0.15, 0);
  for (let y = 0; y < 150; y++) {
    s.px.set(qx, qy - y, M.wood[1]!);
    s.px.set(qx + 1, qy - y, M.wood[3]!);
  }
  clothBanner(s.px, [qx + 3, qy - 146], 12, 42, M.linen, M.dark);
  return done(s);
}

/**
 * The opera stage (เวทีงิ้ว, 10 × 4, ≤ 100 px, red): a raised timber platform with a key-pattern skirt, red columns,
 * a painted back screen with two curtained doors, swags of red cloth, a hip-and-gable roof with big horns.
 */
export function operaStage(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 112, 34);
  const tiles = ROOF_TILES[colour];
  const fl = 22;
  box(s.px, s.P, [0.2, 0.2, w - 0.2, h - 0.2], 0, fl, (u, v, l) => (v > fl - 7 && v < fl - 1 ? snap(M.gold, l, u | 0, v | 0, 0) : snap(M.lacquer, l - 0.1, u | 0, v | 0)), (u, v, l) => snap(M.deck, l + ((u | 0) % 5 === 0 ? -0.15 : 0.05), u | 0, v | 0, 0.2));
  faces(s, [0.2, 0.2, w - 0.2, h - 0.2], fl - 8, fl - 2, (u, v, l) => snap(M.gold, l, u | 0, v | 0, 0));
  // The back screen: a landscape in ink on cream, two curtained doors.
  wall(s.px, s.P, 'left', 0.7, 0.6, w - 0.6, fl, 82, (u, v) => {
    const ridge = 42 + Math.sin(u / 9) * 8 + Math.sin(u / 4) * 3;
    if (v - fl < ridge && v - fl > ridge - 4) return snap(M.dark, 0.6, u | 0, v | 0, 0);
    if (v - fl < ridge) return snap(K.cream, 0.55 - (ridge - (v - fl)) * 0.006, u | 0, v | 0, 0.3);
    return snap(K.cream, 0.75, u | 0, v | 0, 0.2);
  });
  for (const c of [2, w - 2]) wall(s.px, s.P, 'left', 0.71, c - 0.6, c + 0.6, fl, fl + 44, (u, v, l) => snap(M.red, l + 0.15 + ((u | 0) % 4 === 0 ? -0.15 : 0), u | 0, v | 0, 0));
  colonnade(s, h - 0.6, 0.6, w - 0.6, 4, fl, 82, M.lacquer, 0.12);
  column(s.px, s.P, 0.6, 0.6, 0.12, fl, 82);
  column(s.px, s.P, w - 0.6, 0.6, 0.12, fl, 82);
  // Swags of red cloth along the front, a gold band above.
  faces(s, [0.5, 0.5, w - 0.5, h - 0.5], 74, 82, paintedBeam(74, 82, 14));
  for (let k = 0; k < 3; k++) {
    const a = 0.6 + k * ((w - 1.2) / 3);
    const b = a + (w - 1.2) / 3;
    for (let q = 0; q <= 24; q++) {
      const f = q / 24;
      const [x, y] = s.P(a + (b - a) * f, h - 0.5, 74 - Math.sin(f * Math.PI) * 8);
      for (let t = 0; t < 3; t++) s.px.set(x, y + t, snap(M.red, 0.7 - t * 0.15, q, t, 0));
    }
  }
  steps(s.px, s.P, 'right', w - 0.2, h / 2 - 0.7, h / 2 + 0.7, fl, 4, 0.2);
  hipGable(s.px, s.P, [-0.1, 0.0, w + 0.1, h + 0.1], 82, 1.1, 92, 100, tiles);
  horns(s, [-0.1, 0.0, w + 0.1, h + 0.1], 82, tiles, 1.4);
  cornerTassels(s.px, s.P, [-0.1, 0.0, w + 0.1, h + 0.1], 82);
  return done(s);
}

/** The ruined temple (วิหารร้าง, 16 × 10, ≤ 160 px, green): cracked base, faded columns, a broken roof, moss. */
export function ruinedTemple(w: number, h: number, colour: RoofColour): Sprite {
  const s = podiumStage(w, h, 166, 34, 10);
  const tiles = ROOF_TILES[colour];
  plinthK(s, [0.15, 0.15, w - 0.15, h - 0.15], 14);
  const r: Rect = [1.0, 0.8, w - 1.0, h - 2.0];
  plasterWalls(s, r, 14, 90, 'none', 96);
  faces(s, r, 14, 90, (u, v, l) => {
    const crack = Math.abs(valueNoise(u / 3, v / 30) - 0.5) < 0.02;
    const moss = valueNoise(u / 7 + 3, v / 9) > 0.66 && v < 50;
    return crack ? M.dark[2]! : moss ? snap(M.moss, 0.5, u | 0, v | 0) : snap(M.plaster, l - 0.15 + (valueNoise(u / 5, v / 8) - 0.5) * 0.25 - (v > 84 ? 0.3 : 0), u | 0, v | 0);
  });
  door(s.px, s.P, 'left', r[3], w / 2 - 1, w / 2 + 1, 14, 76, M.timber);
  windows(s, 'left', r[3], r[0], r[2], 36, 66, 6, 0.15, 96, [w / 2 - 1.3, w / 2 + 1.3]);
  windows(s, 'right', r[2], r[1], r[3], 36, 66, 3, 0.15, 96);
  colonnade(s, h - 1.4, 1.0, w - 1.0, 6, 14, 90, M.terra, 0.12);
  faces(s, [0.9, 0.7, w - 0.9, h - 1.3], 90, 98, brackets(90, 98));
  steps(s.px, s.P, 'left', h - 0.15, w / 2 - 1.8, w / 2 + 1.8, 14, 3, 0.15);
  hipGable(s.px, s.P, [0.2, 0.0, w - 0.2, h - 0.6], 98, 2.2, 132, 158, tiles);
  // The broken part of the roof: missing tiles showing dark rafters, and loose moss.
  for (let k = 0; k < 2; k++) {
    const [hx, hy] = s.P(4 + k * 6, 3 + k, 130 - k * 10);
    for (let b = -6; b <= 6; b++)
      for (let a = -12; a <= 12; a++) {
        if ((a * a) / 144 + (b * b) / 36 > 1 - hash2(a + k * 9, b) * 0.3) continue;
        s.px.set(hx + a, hy + b, (a + 40) % 5 === 0 ? M.timber[3]! : M.dark[0]!);
      }
  }
  for (let k = 0; k < 40; k++) {
    const [mx, my] = s.P(1 + hash2(k, 1) * (w - 2), hash2(k, 2) * (h - 1), 100 + hash2(k, 3) * 40);
    s.px.rect(mx, my, 2, 1, M.moss[M.moss.length - 2]!);
  }
  return done(s);
}

/** The bell tower (หอระฆัง, 5 × 5, ≤ 216 px, green): a brick base with an arch, an open timber storey with the bell. */
export function bellTower(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 222, 34);
  const tiles = ROOF_TILES[colour];
  const r: Rect = [0.4, 0.4, w - 0.4, h - 0.4];
  faces(s, r, 0, 76, (u, v, l) => {
    const row = Math.floor(v / 4);
    const off = row & 1 ? 4 : 0;
    if (v % 4 < 1 || (u + off) % 8 < 1) return snap(M.stone, l - 0.05, u | 0, v | 0, 0);
    return snap(M.brick, l + (hash2(Math.floor((u + off) / 8) * 7, row) - 0.5) * 0.25 + (valueNoise(u / 6, v / 9) > 0.7 ? -0.15 : 0), u | 0, v | 0);
  });
  // The arch through the base.
  wall(s.px, s.P, 'left', r[3], w / 2 - 0.7, w / 2 + 0.7, 0, 48, (u, v) => {
    const c = (w / 2) * 16;
    const top = 36 + Math.sqrt(Math.max(0, 1 - ((u - c) / 11.2) ** 2)) * 10;
    return v < top ? snap(M.dark, 0.2, u | 0, v | 0) : null;
  });
  trim(s, r, 70, M.dark);
  hipRoof(s, [0.1, 0.1, w - 0.1, h - 0.1], 76, [0.5, 0.5, w - 0.5, h - 0.5], 84, tiles, { ridge: false, horns: 0.7 });
  // The open storey: four columns, a rail, the bronze bell hanging inside.
  const z0 = 84;
  const z1 = 160;
  box(s.px, s.P, [0.6, 0.6, w - 0.6, h - 0.6], z0 - 2, z0, (u, v, l) => snap(M.deck, l, u | 0, v | 0), (u, v, l) => snap(M.deck, l, u | 0, v | 0));
  const [bx, by] = s.P(w / 2, h / 2, z1 - 14);
  for (let y = 0; y < 34; y++) {
    const wdt = 5 + Math.round((y / 34) ** 1.6 * 8);
    for (let a = -wdt; a <= wdt; a++) s.px.set(bx + a, by + y, snap(M.jade, 0.35 + (a / wdt) * 0.3 + (y % 9 === 0 ? -0.2 : 0), a, y, 0));
  }
  s.px.rect(bx - 1, by - 6, 3, 6, M.dark[2]!);
  for (const [x, y] of [
    [0.8, 0.8],
    [w - 0.8, 0.8],
    [0.8, h - 0.8],
    [w - 0.8, h - 0.8],
  ] as const)
    column(s.px, s.P, x, y, 0.11, z0, z1, K.maroon);
  rail(s.px, s.P, 'left', h - 0.6, 0.6, w - 0.6, z0, 14, K.maroon);
  rail(s.px, s.P, 'right', w - 0.6, 0.6, h - 0.6, z0, 14, K.maroon);
  faces(s, [0.6, 0.6, w - 0.6, h - 0.6], z1, z1 + 8, brackets(z1, z1 + 8));
  hipRoof(s, [-0.1, -0.1, w + 0.1, h + 0.1], z1 + 8, [w / 2, h / 2, w / 2, h / 2], 202, tiles, { horns: 1.2, tassels: true });
  finial(s.px, s.P(w / 2, h / 2, 202), 9);
  return done(s);
}

/** The garden pagoda (เจดีย์, 6 × 6, ≤ 190 px, green): three storeys stepping in, glazed skirts, a gold spire. */
export function gardenPagoda(n: number, colour: RoofColour): Sprite {
  const s = stage(n, n, 196, 30);
  const tiles = ROOF_TILES[colour];
  plinthK(s, [-0.1, -0.1, n + 0.1, n + 0.1], 10);
  const story = [40, 34, 28];
  let z = 10;
  for (let k = 0; k < 3; k++) {
    const ins = 0.7 + k * 0.55;
    const r: Rect = [ins, ins, n - ins, n - ins];
    const z1 = z + story[k]!;
    faces(s, r, z, z1, (u, v, l) => snap(M.lacquer, l - (v > z1 - 5 ? 0.3 : 0), u | 0, v | 0));
    for (const side of ['left', 'right'] as const) {
      const at = side === 'left' ? r[3] : r[2];
      const [u0, u1] = side === 'left' ? [r[0], r[2]] : [r[1], r[3]];
      timber(s, side, at, u0, u1, z, z1, (u1 - u0) / 3, [], K.maroon, z1);
      const mid = (u0 + u1) / 2;
      if (k === 0 && side === 'left') door(s.px, s.P, side, at, mid - 0.45, mid + 0.45, z, z1 - 8, M.lacquer);
      else lattice(s.px, s.P, side, at, mid - 0.4, mid + 0.4, z + 8, z1 - 6, 0.55, z1);
    }
    faces(s, r, z1 - 5, z1, brackets(z1 - 5, z1));
    const o = 0.6;
    const last = k === 2;
    const ni = ins + 0.55;
    hipRoof(s, [ins - o, ins - o, n - ins + o, n - ins + o], z1, last ? [n / 2, n / 2, n / 2, n / 2] : [ni, ni, n - ni, n - ni], last ? z1 + 34 : z1 + 12, tiles, {
      ridge: last,
      horns: 1,
      tassels: true,
    });
    z = z1 + 12;
  }
  finial(s.px, s.P(n / 2, n / 2, 10 + 40 + 34 + 28 + 24 + 34), 14);
  return done(s);
}

/** A shrine (ศาล): a stone base, a little red-columned house, an offering table and an incense burner before it. */
export function shrine(w: number, h: number, colour: RoofColour, grave: boolean): Sprite {
  const Z = grave ? 116 : 96;
  const s = stage(w, h, Z, 30);
  const tiles = ROOF_TILES[colour];
  const base = grave ? 16 : 12;
  plinthK(s, [0.2, 0.2, w - 0.2, h - 0.2], base);
  const r: Rect = [0.7, 0.6, w - 0.7, h - 1.6];
  const z1 = grave ? 66 : 54;
  if (grave) faces(s, r, base, z1, (u, v, l) => snap(M.stone, l - 0.05 + (hash2((u / 9) | 0, (v / 5) | 0) - 0.5) * 0.2, u | 0, v | 0));
  else plasterWalls(s, r, base, z1, 'none', z1 + 4);
  wall(s.px, s.P, 'left', r[3], w / 2 - 0.55, w / 2 + 0.55, base, z1 - 8, (u, v) => snap(M.dark, 0.25 + (v - base) / 200, u | 0, v | 0));
  // The god's tablet glowing in the dark niche.
  wall(s.px, s.P, 'left', r[3] + 0.01, w / 2 - 0.15, w / 2 + 0.15, base + 8, base + 26, (u, v) => (v > base + 22 ? M.gold[3]! : snap(M.red, 0.6, u | 0, v | 0, 0)));
  column(s.px, s.P, r[0] + 0.05, h - 1.2, 0.1, base, z1, M.lacquer);
  column(s.px, s.P, r[2] - 0.05, h - 1.2, 0.1, base, z1, M.lacquer);
  faces(s, [r[0] - 0.1, r[1] - 0.1, r[2] + 0.1, h - 1.1], z1, z1 + 6, brackets(z1, z1 + 6));
  // Red cloth over the door and the offering table with its burner.
  wall(s.px, s.P, 'left', h - 1.15, r[0], r[2], z1 - 8, z1, (u, v) => snap(M.red, 0.6 + ((u | 0) % 6 < 3 ? 0.1 : -0.1), u | 0, v | 0, 0));
  box(s.px, s.P, [w / 2 - 0.7, h - 0.95, w / 2 + 0.7, h - 0.45], 0, base + 6, (u, v, l) => snap(M.lacquer, l, u | 0, v | 0), (u, v, l) => snap(M.red, l, u | 0, v | 0, 0));
  const [ix, iy] = s.P(w / 2, h - 0.7, base + 6);
  blob(s.px, ix, iy - 3, 4, 3, M.gold, 0.55);
  for (let k = 0; k < 3; k++) s.px.rect(ix - 2 + k * 2, iy - 11, 1, 6, M.red[3]!);
  hipRoof(s, [0.2, 0.1, w - 0.2, h - 0.8], z1 + 6, grave ? [1.2, (h - 0.7) / 2, w - 1.2, (h - 0.7) / 2] : [w / 2, (h - 0.7) / 2, w / 2, (h - 0.7) / 2], Z - 6, tiles, { horns: 1, tassels: true });
  if (!grave) finial(s.px, s.P(w / 2, (h - 0.7) / 2, Z - 6), 4);
  return done(s);
}

/** The wayside pavilion (ศาลา, 4 × 4, ≤ 120 px, green): a stone floor, four red columns, benches, a pyramid roof. */
export function waysidePavilion(n: number, colour: RoofColour, v: number): Sprite {
  const s = stage(n, n, 126, 30);
  const tiles = ROOF_TILES[colour];
  plinthK(s, [0.0, 0.0, n, n], 10);
  steps(s.px, s.P, 'left', n, n / 2 - 0.6, n / 2 + 0.6, 10, 2, 0.16);
  const e = 0.4;
  const Hc = 74;
  column(s.px, s.P, e, e, 0.12, 10, 10 + Hc);
  column(s.px, s.P, n - e, e, 0.12, 10, 10 + Hc);
  column(s.px, s.P, e, n - e, 0.12, 10, 10 + Hc);
  // A stone tablet (v even) or a stone table (v odd) inside.
  if (v % 2 === 0) {
    box(s.px, s.P, [n / 2 - 0.5, n / 2 - 0.15, n / 2 + 0.5, n / 2 + 0.15], 10, 14, (u, vv, l) => snap(M.stone, l, u | 0, vv | 0, 0.2));
    box(s.px, s.P, [n / 2 - 0.4, n / 2 - 0.08, n / 2 + 0.4, n / 2 + 0.08], 14, 46, (u, vv, l) => snap(M.stone, l + 0.1 + ((vv | 0) % 6 === 0 && (u | 0) % 3 ? -0.25 : 0), u | 0, vv | 0, 0));
  } else {
    box(s.px, s.P, [n / 2 - 0.12, n / 2 - 0.12, n / 2 + 0.12, n / 2 + 0.12], 10, 22, (u, vv, l) => snap(M.stone, l, u | 0, vv | 0, 0.2));
    box(s.px, s.P, [n / 2 - 0.45, n / 2 - 0.45, n / 2 + 0.45, n / 2 + 0.45], 22, 26, (u, vv, l) => snap(M.stone, l, u | 0, vv | 0, 0.2));
  }
  // Benches with lean-back rails (美人靠) on the two near sides, open at the steps.
  for (const [side, at] of [
    ['left', n - e],
    ['right', n - e],
  ] as const) {
    const parts: [number, number][] = side === 'left' ? [[e, n / 2 - 0.65], [n / 2 + 0.65, n - e]] : [[e, n - e]];
    for (const [a, b] of parts) {
      if (side === 'left') box(s.px, s.P, [a, at - 0.3, b, at], 10, 22, (u, vv, l) => snap(M.lacquer, l, u | 0, vv | 0), (u, vv, l) => snap(M.deck, l, u | 0, vv | 0));
      else box(s.px, s.P, [at - 0.3, a, at, b], 10, 22, (u, vv, l) => snap(M.lacquer, l, u | 0, vv | 0), (u, vv, l) => snap(M.deck, l, u | 0, vv | 0));
      rail(s.px, s.P, side, at, a, b, 22, 12, M.lacquer, 4);
    }
  }
  column(s.px, s.P, n - e, n - e, 0.12, 10, 10 + Hc);
  faces(s, [e - 0.1, e - 0.1, n - e + 0.1, n - e + 0.1], 10 + Hc - 8, 10 + Hc, paintedBeam(10 + Hc - 8, 10 + Hc, 12));
  hipRoof(s, [-0.5, -0.5, n + 0.5, n + 0.5], 10 + Hc, [n / 2, n / 2, n / 2, n / 2], 112, tiles, { horns: 1.2, tassels: true });
  finial(s.px, s.P(n / 2, n / 2, 112), 6);
  return done(s);
}

/** A camp tent (กระโจม, 4 × 4, ≤ 64 px): canvas over a ridge pole, guy ropes, a flap open on the front. */
export function tent(n: number, v: number): Sprite {
  const s = stage(n, n, 70, 24);
  const cloth = v % 2 ? K.cream : K.ochre;
  const ridgeZ = 56;
  // Two slopes along x, the gable end at the front-right is the open flap.
  const L = 0.4;
  const R = n - 0.4;
  const quadFill = (pts: readonly [number, number, number][], l: number) => {
    if (faceRec.on && l > 0) faceRec.faces.push({ kind: pts.length === 3 ? 'wall' : 'roof', pts: pts.length === 3 ? [...pts, pts[2]!] : pts });
    const sp = pts.map(([x, y, z]) => s.P(x, y, z));
    const xs = sp.map((p) => p[0]);
    const ys = sp.map((p) => p[1]);
    for (let y = Math.floor(Math.min(...ys)); y <= Math.ceil(Math.max(...ys)); y++)
      for (let x = Math.floor(Math.min(...xs)); x <= Math.ceil(Math.max(...xs)); x++) {
        let inside = false;
        for (let i = 0, j = sp.length - 1; i < sp.length; j = i++) {
          const [xi, yi] = sp[i]!;
          const [xj, yj] = sp[j]!;
          if (yi > y + 0.5 !== yj > y + 0.5 && x + 0.5 < ((xj - xi) * (y + 0.5 - yi)) / (yj - yi) + xi) inside = !inside;
        }
        if (inside) s.px.set(x, y, snap(cloth, l + ((x + y * 2) % 9 === 0 ? -0.15 : 0) + (valueNoise(x / 4, y / 4) - 0.5) * 0.1, x, y, 0.2));
      }
  };
  quadFill([[L, 0.3, 0], [R, 0.3, 0], [R, n / 2, ridgeZ], [L, n / 2, ridgeZ]], 0.45);
  quadFill([[L, n - 0.3, 0], [R, n - 0.3, 0], [R, n / 2, ridgeZ], [L, n / 2, ridgeZ]], 0.5);
  quadFill([[R, 0.3, 0], [R, n - 0.3, 0], [R, n / 2, ridgeZ]], 0.72);
  // The open flap: a dark triangle, the flap tied back.
  quadFill([[R + 0.01, n / 2 - 0.6, 0], [R + 0.01, n / 2 + 0.6, 0], [R + 0.01, n / 2, ridgeZ - 18]], 0.0);
  const [fx, fy] = s.P(R, n / 2, ridgeZ - 18);
  for (let k = 0; k < 18; k++) s.px.set(fx, fy + k, M.dark[1]!);
  const [px0, py0] = s.P(R, n / 2, ridgeZ);
  for (let k = 0; k < 8; k++) s.px.set(px0, py0 - k, M.wood[2]!);
  // Guy ropes and pegs.
  for (const [a, b] of [
    [s.P(R, n / 2, ridgeZ), s.P(n + 0.3, n / 2, 0)],
    [s.P(L, n / 2, ridgeZ), s.P(L - 0.4, n + 0.1, 0)],
  ] as const) {
    for (let k = 0; k <= 40; k++) s.px.set(a[0] + (b[0] - a[0]) * (k / 40), a[1] + (b[1] - a[1]) * (k / 40), M.wood[4]!);
  }
  return done(s);
}
